import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    TablePagination,
    TextField,
    Button,
    Snackbar,
    Alert,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Breadcrumbs,
    Link,
} from '@mui/material';
import TableComponent from '../../widgets/TableComponent';
import RequestService from '../../services/RequestService';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import RewardBadge from '../../widgets/RewardBadge';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import UserContentReviewDialog from '../../widgets/usercontents/UserContentReviewDialog';

const PurchaseHistoryPage = () => {
    const { user, hasPermission } = useContext(UserContext);

    const [contents, setContents] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [userId, setuserId] = useState('');
    const [users, setUsers] = useState([]);
    const [groupId, setGroupId] = useState('');
    const [groups, setGroups] = useState([]);
    const [completed, setCompleted] = useState('');
    const [userContent, setSelectedUserContent] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchHistory = async () => {
        try {
            const data = await RequestService.getCompletedsPurchasesWithFilters(
                user.company.id,
                {
                    page,
                    size: rowsPerPage,
                    userId,
                    groupId,
                    completed: completed !== '' ? completed : undefined,
                }
            );

            setContents(data.content || []);
            setTotalElements(data.totalElements || 0);
        } catch (error) {
            console.error('Erro ao carregar histórico de conteúdos:', error);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, [page, rowsPerPage]);

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const userData = await RequestService.getUsersByCompany(
                    user.company.id
                );
                setUsers(Array.isArray(userData) ? userData : []);

                const groupData = await RequestService.getGroups(
                    user.company.id
                );
                setGroups(Array.isArray(groupData) ? groupData : []);
            } catch (error) {
                console.error('Erro ao carregar filtros:', error);
            }
        };

        fetchFilters();
    }, [user.company.id]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleSuccessReview = (text) => {
        setSnackbarMessage(text);
        setSnackbarOpen(true);
    };

    const columns = [
        {
            label: 'Produto',
            field: 'name',
            renderCell: (_, row) => row.product.name || '-',
        },
        {
            label: 'Usuário',
            field: 'user',
            renderCell: (_, row) => row.user.name || '-',
        },
        {
            label: 'Status',
            field: 'completed',
            renderCell: (_, row) => (
                <Typography
                    variant="body2"
                    sx={{ ml: 0.5, color: row.completed ? '#1F83FF' : 'red' }}
                >
                    {row.completed ? 'Concluído' : 'Rejeitado'}
                </Typography>
            ),
        },
        {
            label: 'Moedas',
            field: 'coin',
            renderCell: (_, row) => (
                <RewardBadge
                    icon={
                        <CoinIcon
                            style={{ color: '#134F99', fontSize: '24px' }}
                        />
                    }
                    value={row.product?.price}
                />
            ),
        },
    ];

    const renderMobileLayout = () => {
        if (contents.length === 0) {
            return <Typography>Nenhum conteúdo encontrado.</Typography>;
        }

        return (
            <Box>
                {contents.map((item) => (
                    <Box
                        key={item.id}
                        sx={{
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            padding: 2,
                            marginBottom: 2,
                        }}
                    >
                        <Typography variant="body2">
                            Usuário: {item.user?.name || '-'}
                        </Typography>
                        <Typography variant="body2">
                            Recompensa: {item.product?.name || '-'}
                        </Typography>
                        <Typography variant="body2">
                            Aprovado: {item.completed ? '✅' : '❌'}
                        </Typography>
                        <RewardBadge
                            icon={
                                <CoinIcon
                                    style={{
                                        color: '#134F99',
                                        fontSize: '24px',
                                    }}
                                />
                            }
                            value={item.product?.price}
                        />
                    </Box>
                ))}

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                />
            </Box>
        );
    };

    const renderDesktopLayout = () => (
        <Box>
            <TableComponent columns={columns} rows={contents} />

            <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20, 50]}
            />
        </Box>
    );

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        GameUP
                    </Link>
                    <Link underline="hover" color="inherit">
                        Missões
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Histórico
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Histórico de recompensas
                </Typography>
            </Grid>

            <Box sx={{ my: 2 }}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    {/* Filtro Usuário */}
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Usuário</InputLabel>
                        <Select
                            value={userId}
                            label="Usuário"
                            onChange={(e) => setuserId(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {users.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {u.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Grupo</InputLabel>
                        <Select
                            value={groupId}
                            label="Grupo"
                            onChange={(e) => setGroupId(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {groups.map((g) => (
                                <MenuItem key={g.id} value={g.id}>
                                    {g.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={completed}
                            label="Status"
                            onChange={(e) => setCompleted(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value={true}>Concluído</MenuItem>
                            <MenuItem value={false}>Rejeitado</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Botão de aplicar */}
                    <Button
                        variant="contained"
                        sx={{ height: 45 }}
                        onClick={() => {
                            setPage(0);
                            fetchHistory();
                        }}
                    >
                        Aplicar Filtros
                    </Button>
                </Box>
            </Box>

            {isMobile ? renderMobileLayout() : renderDesktopLayout()}

            <UserContentReviewDialog
                open={isDialogOpen}
                onSucessApprove={(text) => {
                    handleSuccessReview(text);
                    setIsDialogOpen(false);
                    setSelectedUserContent(null);
                    fetchHistory(); // Atualiza os dados
                }}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setIsDialogOpen(false);
                        setSelectedUserContent(null);
                    }
                }}
                content={userContent}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PurchaseHistoryPage;
